import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const buttonVariants = cva(
  // Base — layout, transitions, focus, disabled
  [
    "relative inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium",
    "transition-[background-color,border-color,color,box-shadow,transform] duration-fast ease-standard",
    "disabled:pointer-events-none disabled:opacity-50",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--brand-background)]",
    "active:translate-y-[0.5px]",
  ],
  {
    variants: {
      variant: {
        // MV2-849 (ds/Main, WhiteLabelAction): the filled control is the
        // derived action set — the tenant's hue, legible on this theme's
        // page, with an edge only when contrast demands it.
        // Board kit `.btn` (ds/Main): flat — no drop shadow, the edge only when
        // contrast demands it; hover darkens, nothing lifts (Kranthi 9 Sep: sleek).
        primary: [
          "border border-[color:var(--action-edge)] bg-[color:var(--action)] text-[color:var(--action-fg)]",
          "hover:bg-[color:var(--action-hover)]",
        ],
        secondary: [
          "bg-[color:var(--brand-secondary)] text-[color:var(--brand-secondary-fg)]",
          "hover:bg-[color:var(--brand-secondary-hover)]",
        ],
        outline: [
          "border border-gray-300 bg-surface text-gray-800",
          "hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900",
        ],
        ghost: "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
        destructive:
          "bg-[color:var(--danger)] text-white hover:opacity-90",
        // Floating button over map / images — has to win against any background
        floating: [
          "bg-gray-0 text-gray-900 border border-gray-200 shadow-md",
          "hover:bg-gray-50 hover:shadow-lg",
        ],
      },
      // Sizes are the board kit's: `sm` is `.btn` (32 · 13 px · radius 8), the
      // page-scale button every list and header uses; `xs` is `.btn.sm`
      // (26 · 12 px · radius 6) for table rows and card headers; `md` / `lg`
      // stay for forms, dialogs and mobile.
      size: {
        xs: "h-[26px] px-[9px] text-[12px] rounded-[6px]",
        sm: "h-8 px-[13px] text-[13px] rounded-[8px]",
        md: "h-9 px-4 text-[13px] rounded-[8px]",
        lg: "h-11 px-6 text-base rounded-lg",
        icon: "h-8 w-8 rounded-[8px]",
        "icon-sm": "h-[26px] w-[26px] rounded-[6px]",
        // MV2-985 — a real 44px touch target for icon-only row actions on
        // mobile (`icon-sm`'s 26px is a mouse-pointer size); desktop rows
        // keep `icon-sm`.
        "icon-lg": "h-11 w-11 rounded-[10px]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
